import { useEffect, useRef, useState } from 'react'
import { apiRouletteError, loadApiRouletteHistory, loadApiRouletteSlots } from './api'
import type { ApiRouletteLog, ApiRoulettePage, ApiRouletteSlotLog } from './types'
import { ApiRouletteSectionHeading } from './ApiRouletteSectionHeading'
import styles from './ApiRoulette.module.css'

const reasons: Record<string, string> = {
  won: 'Primeiro giro válido',
  already_awarded: 'Horário já contemplado',
  before_window: 'Sem horário vigente no dia',
  slot_disabled: 'Horário desativado',
  roulette_disabled: 'Roleta desativada',
  campaign_closed: 'Campanha fora do período ativo',
}
const dateTime = (value: string | null, timezone: string) =>
  value
    ? new Date(`${value.replace(' ', 'T')}Z`).toLocaleString('pt-BR', {
        timeZone: timezone,
      })
    : '—'

export function ApiRouletteLogs({
  campaignId,
  selectedSlot,
  onSlotChange,
  revision,
  timezone,
}: {
  campaignId: number
  selectedSlot: string
  onSlotChange: (slot: string) => void
  revision: number
  timezone: string
}) {
  const [history, setHistory] = useState<ApiRoulettePage<ApiRouletteLog> | null>(null)
  const [slots, setSlots] = useState<ApiRoulettePage<ApiRouletteSlotLog> | null>(null)
  const [status, setStatus] = useState('')
  const [externalId, setExternalId] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [slotPage, setSlotPage] = useState(1)
  const [refresh, setRefresh] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const rootRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (selectedSlot)
      rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [selectedSlot])

  useEffect(() => {
    const controller = new AbortController()
    const query = new URLSearchParams({ page: String(page) })
    if (selectedSlot) query.set('slot_id', selectedSlot)
    if (status) query.set('status', status)
    if (search) query.set('external_id', search)
    const load = async () => {
      setLoading(true)
      setError(null)
      setHistory(null)
      return Promise.all([
        loadApiRouletteHistory(campaignId, query, controller.signal),
        loadApiRouletteSlots(campaignId, slotPage, controller.signal),
      ])
    }
    void load()
      .then(([nextHistory, nextSlots]) => {
        if (!controller.signal.aborted) {
          setHistory(nextHistory)
          setSlots(nextSlots)
        }
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) setError(apiRouletteError(err))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [campaignId, page, selectedSlot, status, search, slotPage, refresh, revision])

  const chooseSlot = (id: string) => {
    setPage(1)
    onSlotChange(id)
  }
  return (
    <section ref={rootRef} className={`${styles.section} ${styles.auditSection}`}>
      <ApiRouletteSectionHeading
        step="04"
        title="Participações e auditoria"
        actions={
          <button
            type="button"
            disabled={loading}
            onClick={() => setRefresh((value) => value + 1)}
          >
            Atualizar logs
          </button>
        }
      >
        Consulte quem recebeu autorização, quem girou e quem ganhou. O histórico permanece
        disponível após desativar um horário.
      </ApiRouletteSectionHeading>
      <details>
        <summary>Resumo por horário ({slots?.total ?? 0})</summary>
        <div className={styles.tableWrap}>
          <table>
            <thead>
              <tr>
                <th>Dia / horário</th>
                <th>Giros</th>
                <th>Ganhadores</th>
                <th>Perdas</th>
                <th>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {slots?.items.map((slot) => (
                <tr key={slot.id}>
                  <td>
                    {slot.date} · {slot.time.slice(0, 5)}{' '}
                    {slot.archived_at
                      ? '(arquivado)'
                      : !slot.is_active
                        ? '(inativo)'
                        : ''}
                  </td>
                  <td>{slot.attempts_count}</td>
                  <td>{slot.winners_count}</td>
                  <td>{Number(slot.attempts_count) - Number(slot.winners_count)}</td>
                  <td>
                    <button type="button" onClick={() => chooseSlot(slot.id)}>
                      Ver participantes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.toolbar}>
          <button
            type="button"
            disabled={slotPage <= 1 || loading}
            onClick={() => setSlotPage(slotPage - 1)}
          >
            Anterior
          </button>
          <span>
            {slotPage} / {slots?.last_page ?? 1}
          </span>
          <button
            type="button"
            disabled={!slots || slotPage >= slots.last_page || loading}
            onClick={() => setSlotPage(slotPage + 1)}
          >
            Próxima
          </button>
        </div>
      </details>
      <div className={styles.toolbar}>
        <label>
          Resultado
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
          >
            <option value="">Todos</option>
            <option value="won">Ganhou</option>
            <option value="lost">Não ganhou</option>
            <option value="not_played">Autorizado, não girou</option>
          </select>
        </label>
        <div className={styles.toolbar}>
          <label>
            ID do participante
            <input
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              maxLength={128}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  setSearch(externalId.trim())
                  setPage(1)
                }
              }}
            />
          </label>
          <button
            type="button"
            onClick={() => {
              setSearch(externalId.trim())
              setPage(1)
            }}
          >
            Buscar
          </button>
        </div>
        {selectedSlot && (
          <button type="button" onClick={() => chooseSlot('')}>
            Mostrar log geral
          </button>
        )}
      </div>
      {selectedSlot && (
        <p role="status">Exibindo somente os giros do horário selecionado.</p>
      )}
      {error && <p role="alert">{error}</p>}
      {loading && <p role="status">Atualizando participações…</p>}
      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>Participante</th>
              <th>Horário configurado</th>
              <th>Resultado</th>
              <th>Registro</th>
              <th>Auditoria</th>
            </tr>
          </thead>
          <tbody>
            {history?.items.map((item) => (
              <tr key={item.ticket_id}>
                <td>
                  <strong>{item.participant_name}</strong>
                  <br />
                  {item.external_id}
                  <br />
                  {item.participant_email}
                </td>
                <td>
                  {item.local_date
                    ? `${item.local_date} · ${item.local_time?.slice(0, 5)}`
                    : 'Sem intervalo vinculado'}
                </td>
                <td>
                  <span className={item.status === 'won' ? styles.won : styles.badge}>
                    {item.status === 'won'
                      ? 'Ganhou'
                      : item.status === 'lost'
                        ? 'Não ganhou'
                        : 'Não girou'}
                  </span>
                  <br />
                  {item.reason
                    ? (reasons[item.reason] ?? item.reason)
                    : 'Autorização emitida'}
                  {item.position && (
                    <>
                      <br />
                      Fatia {item.position}
                      {item.slice_label ? ` · ${item.slice_label}` : ''}
                    </>
                  )}
                </td>
                <td>
                  {item.played_at
                    ? dateTime(item.played_at, timezone)
                    : `Emitida: ${dateTime(item.authorized_at, timezone)}`}
                  <br />
                  <small>{timezone}</small>
                </td>
                <td>
                  <details>
                    <summary>Ver registro</summary>
                    <p>
                      Giro #{item.attempt_id ?? '—'} · revisão{' '}
                      {item.configuration_version ?? '—'}
                    </p>
                    <p>Origem: {item.origin}</p>
                    {item.slice_description && <p>Prêmio: {item.slice_description}</p>}
                    <p>IP: {item.ip_address ?? '—'}</p>
                    <p>{item.user_agent}</p>
                    <p>Autorização: {item.ticket_id}</p>
                  </details>
                </td>
              </tr>
            ))}
            {!loading && !history?.items.length && (
              <tr>
                <td colSpan={5}>Nenhuma participação neste recorte.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className={styles.toolbar}>
        <button
          type="button"
          disabled={page <= 1 || loading}
          onClick={() => setPage(page - 1)}
        >
          Anterior
        </button>
        <span>
          {history?.total ?? 0} registros · {page} / {history?.last_page ?? 1}
        </span>
        <button
          type="button"
          disabled={!history || page >= history.last_page || loading}
          onClick={() => setPage(page + 1)}
        >
          Próxima
        </button>
      </div>
      <p>
        “Não girou” significa que a integração emitiu uma autorização e ela ainda não foi
        utilizada. Visitantes não identificados pelo site externo não entram nesse
        relatório.
      </p>
    </section>
  )
}
